import React, { useState } from "react";
import { IDatabaseLineItem } from "../types";
import { LineItemPreviewPanel } from "./LineItemPreviewPanel";
import { DeleteLineItemDialog } from "./DeleteLineItemDialog";
import { toastNotify } from "../../../../shared/client/FrontendUtils";

interface Props {
	item: IDatabaseLineItem;
	index: number;
	isExpanded: boolean;
	showRecycleBin: boolean;
	showCopyButton: boolean;
	showXactimateButton: boolean;
	onToggleExpand: () => void;
	onEdit: () => void;
	onDelete: () => Promise<void>;
	onRestore: () => Promise<void>;
	onCopy: () => void;
	onInfoClick: () => void;
	onXactimateClick: () => void;
}

const LineItemListItem: React.FC<Props> = ({
	item,
	index,
	isExpanded,
	showRecycleBin,
	showCopyButton,
	showXactimateButton,
	onToggleExpand,
	onEdit,
	onDelete,
	onRestore,
	onCopy,
	onInfoClick,
	onXactimateClick,
}) => {
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);

	return (
		<>
			<div
				key={`${index}-0`}
				className={`line-item-container self-stretch px-6 py-5  border-l border-r border-b border-gray-200 justify-between items-center inline-flex cursor-pointer ${
					showRecycleBin ? "bg-red-50" : "bg-gray-50"
				}`}
				onClick={onToggleExpand}
			>
				<div className="grow shrink basis-0 flex-col justify-start items-start gap-4">
					<div className="h-[32px] justify-start items-center gap-4 flex w-full">
						<div>{item.index ?? "NI"}</div>
						<div>{new Date(item.createTime ?? 0).toLocaleString()}</div>
						<div className="justify-start items-center gap-2 flex">
							{item.elements?.map((element, ind) => {
								let errorConfigured = false;
								if (element.defaultValue && element.allowEmptyValue) {
									errorConfigured = true;
								}
								return (
									<React.Fragment key={ind}>
										{element.type === "variable" && (
											<div
												className={`px-2 ${
													errorConfigured ? "bg-orange-100" : "bg-sky-100"
												} rounded justify-start items-center gap-2.5 flex`}
											>
												<div className="text-justify text-gray-900 text-lg font-normal font-['Hanken Grotesk'] leading-[25.20px]">
													{element.value}
												</div>
											</div>
										)}
										{element.type !== "variable" && (
											<div className="rounded justify-start items-center gap-2.5 flex">
												<div className="text-justify text-gray-900 text-lg font-normal font-['Hanken Grotesk'] leading-[25.20px]">
													{element.value}
												</div>
											</div>
										)}
									</React.Fragment>
								);
							})}
						</div>
					</div>
					<div className="flex gap-2 ml-4 mt-2">
						{item.category && (
							<div className="px-2 py-1 bg-blue-100 rounded text-blue-700 text-sm font-medium">{item.category}</div>
						)}
						{item.scopes &&
							item.scopes.map((scope) => (
								<div key={scope} className="px-2 py-1 bg-gray-200 rounded text-gray-700 text-sm font-medium">
									{scope}
								</div>
							))}
					</div>
				</div>
				<div className="w-[280px] flex items-center justify-between ml-8">
					<div className="flex items-center gap-2">
						{!showRecycleBin && showXactimateButton && (
							<div className="only-on-hover">
								<div
									className="w-8 h-8 bg-white rounded-full shadow border border-gray-300 justify-center items-center flex cursor-pointer text-xs font-bold text-stone-700"
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
										onXactimateClick();
									}}
									title="Xactimate Mappings"
								>
									x8
								</div>
							</div>
						)}
						<div className="only-on-hover">
							<div
								className="w-8 h-8 bg-white rounded-full shadow border border-gray-300 justify-center items-center flex cursor-pointer"
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									onInfoClick();
								}}
								title="Info"
							>
								<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
									<path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
								</svg>
							</div>
						</div>
					</div>
					<div className="only-on-hover">
						<div
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
								navigator.clipboard
									.writeText(item.id)
									.then(() => toastNotify(`Copied ${item.id} to clipboard`))
									.catch(() => toastNotify("Failed to copy ID", true));
							}}
							className="h-9 px-2 bg-white rounded-lg border border-gray-200 justify-center items-center inline-flex cursor-pointer"
						>
							<div className="text-gray-900 text-sm font-normal font-['Hanken Grotesk'] leading-tight">
								Copy ID
							</div>
						</div>
					</div>
					<div className="flex items-center gap-2">
						{!showRecycleBin && (
							<div className="only-on-hover">
								<div
									className="w-8 h-8 bg-white rounded-lg shadow border border-gray-300 justify-center items-center flex"
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
										onEdit();
									}}
								>
									<img src="/assets/ui/edit-03.svg" className="w-5 h-5 relative" title="Edit" />
								</div>
							</div>
						)}
						{showCopyButton && (
							<div className="only-on-hover">
								<div
									className="w-8 h-8 bg-white rounded-lg shadow border border-gray-300 justify-center items-center flex"
									onClick={(e) => {
										e.stopPropagation();
										e.preventDefault();
										onCopy();
									}}
								>
									<img src="/assets/ui/expand-03.svg" className="w-5 h-5 relative" title="Copy" />
								</div>
							</div>
						)}
						<div className="only-on-hover">
							<div
								className={`w-8 h-8 rounded-lg shadow border justify-center items-center flex ${
									!showRecycleBin ? "bg-red-600 border-red-600" : "bg-white border-gray-300"
								}`}
								onClick={async (e) => {
									e.stopPropagation();
									e.preventDefault();
									if (showRecycleBin) {
										const confirmResult = window.confirm("Are you sure you want to restore this line item?");
										if (confirmResult) {
											await onRestore();
										}
									} else {
										setShowDeleteDialog(true);
									}
								}}
							>
								<img
									src={`/assets/ui/${showRecycleBin ? "flip-backward" : "trash-01"}.svg`}
									title={showRecycleBin ? "Restore" : "Delete"}
									className="w-5 h-5 relative"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			{isExpanded ? (
				<div
					key={`${index}-1`}
					className="self-stretch px-6 py-8 justify-start items-start gap-4 inline-flex min-h-[200px]"
				>
					<LineItemPreviewPanel lineItemToProcess={item.elements} />
				</div>
			) : null}
			{showDeleteDialog && (
				<DeleteLineItemDialog
					lineItem={item}
					onClose={() => setShowDeleteDialog(false)}
					onConfirmed={onDelete}
				/>
			)}
		</>
	);
};

export default LineItemListItem;
